import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createPortal } from 'react-dom';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';

import { BIBLE_BOOKS, getChaptersForBook, getVersesForChapter, getVerseText, getBookInfo } from '../utils/bibleData';
import { useTranslation } from '../contexts/TranslationContext';
import { getChapterNoteCounts } from '../utils/chapterNotes';
import { useListenState, readListenPoint } from '../hooks/useListenState';
import { useListenDataOptimized } from '../hooks/useListenDataOptimized';
import { getPlanSequence, findSequenceIndex } from '../utils/planPlayback';

// Import optimized components
import TopNavigation from '../components/Listen/TopNavigation';
import PassageSelector from '../components/Listen/PassageSelector';
import BottomPlayerSheet from '../components/Listen/BottomPlayerSheet';
import BibleTextDisplay from '../components/Listen/BibleTextDisplay';
import MultiAudioPlayer from '../components/MultiAudioPlayer';
import StudyNoteEditor from '../components/StudyNoteEditor';
import StudyNotesDrawer from '../components/StudyNotesDrawer';
import AddToPlanPopover from '../components/Listen/AddToPlanPopover';

function Listen() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { version: translationVersion } = useTranslation();

  // Use optimized state management
  const { state, actions } = useListenState();
  const [familyMemberPermissions, setFamilyMemberPermissions] = useState({});
  
  // Use optimized data fetching
  const data = useListenDataOptimized(currentUser, state.userFamilyIds);
  
  // Bottom player sheet (mobile) — expanded = shows filters/toggles
  const [bottomSheetExpanded, setBottomSheetExpanded] = useState(false);

  // Audio player ref
  const playerRef = useRef(null);
  // Guards the one-time initial-listen-point decision (runs after data loads).
  const hasAppliedLastListenRef = useRef(false);
  // Set right before navigating to a plan sequence item with a narrower verse
  // range than the whole chapter (a Passages Plan passage) — consumed once by
  // the verse-range effect below so it doesn't get stomped back to the full
  // chapter range on the very next render.
  const pendingVerseRangeRef = useRef(null);
  // True for exactly one MultiAudioPlayer re-init cycle, right after
  // continuous-plan playback auto-advances to the next item — makes the new
  // queue start playing immediately instead of waiting for a manual tap.
  const [autoPlayNextQueue, setAutoPlayNextQueue] = useState(false);
  // Tracks which book_chapter we've already logged a listen event for this session.
  const listenLoggedRef = useRef(new Set());

  // Playback preference: trim leading/trailing silence on each verse.
  // Init synchronously from localStorage so the player has it on first play,
  // then reconcile with the user's saved preference once it loads.
  const [trimSilence, setTrimSilence] = useState(() => {
    try {
      return localStorage.getItem('fb_trimSilence') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const pref = currentUser?.playbackPreferences?.trimSilence;
    if (typeof pref === 'boolean') {
      setTrimSilence(pref);
      try {
        localStorage.setItem('fb_trimSilence', pref ? '1' : '0');
      } catch {}
    }
  }, [currentUser?.playbackPreferences?.trimSilence]);

  const handleSetTrimSilence = useCallback(
    (val) => {
      setTrimSilence(val);
      try {
        localStorage.setItem('fb_trimSilence', val ? '1' : '0');
      } catch {}
      if (currentUser?.uid) {
        updateDoc(doc(db, 'users', currentUser.uid), {
          'playbackPreferences.trimSilence': val,
        }).catch((e) => console.warn('Failed to save trimSilence preference:', e));
      }
    },
    [currentUser?.uid]
  );

  const [playbackRate, setPlaybackRate] = useState(() => {
    try {
      return parseFloat(localStorage.getItem('fb_playbackRate') || '1') || 1.0;
    } catch {
      return 1.0;
    }
  });

  useEffect(() => {
    const pref = currentUser?.playbackPreferences?.playbackRate;
    if (typeof pref === 'number') {
      setPlaybackRate(pref);
      try { localStorage.setItem('fb_playbackRate', String(pref)); } catch {}
    }
  }, [currentUser?.playbackPreferences?.playbackRate]);

  const handleSetPlaybackRate = useCallback(
    (val) => {
      setPlaybackRate(val);
      try { localStorage.setItem('fb_playbackRate', String(val)); } catch {}
      if (currentUser?.uid) {
        updateDoc(doc(db, 'users', currentUser.uid), {
          'playbackPreferences.playbackRate': val,
        }).catch((e) => console.warn('Failed to save playbackRate preference:', e));
      }
    },
    [currentUser?.uid]
  );

  // Playback preference: client-side voice enhancement via Web Audio API.
  // Global, set in Profile settings (defaults to on) — not editable here.
  const [enhanceAudio, setEnhanceAudio] = useState(() => {
    try {
      const stored = localStorage.getItem('fb_enhanceAudio');
      return stored === null ? true : stored === '1';
    } catch { return true; }
  });

  useEffect(() => {
    const pref = currentUser?.playbackPreferences?.enhanceAudio;
    if (typeof pref === 'boolean') {
      setEnhanceAudio(pref);
      try { localStorage.setItem('fb_enhanceAudio', pref ? '1' : '0'); } catch {}
    }
  }, [currentUser?.playbackPreferences?.enhanceAudio]);

  // Memoized derived state
  const availableChapters = useMemo(() => 
    getChaptersForBook(state.selectedBook), 
    [state.selectedBook]
  );

  const availableVerses = useMemo(() =>
    getVersesForChapter(state.selectedBook, state.selectedChapter),
    [state.selectedBook, state.selectedChapter, translationVersion]
  );

  const currentPassageText = useMemo(() => {
    if (state.startVerse && state.endVerse && state.startVerse !== state.endVerse) {
      const verses = [];
      for (let v = state.startVerse; v <= state.endVerse; v++) {
        verses.push(getVerseText(state.selectedBook, state.selectedChapter, v));
      }
      return verses.join(' ');
    } else if (state.startVerse) {
      return getVerseText(state.selectedBook, state.selectedChapter, state.startVerse);
    } else {
      const verses = getVersesForChapter(state.selectedBook, state.selectedChapter);
      return verses.map(v => getVerseText(state.selectedBook, state.selectedChapter, v)).join(' ');
    }
  }, [state.selectedBook, state.selectedChapter, state.startVerse, state.endVerse, translationVersion]);

  // Readers who actually have recordings in the current chapter (after family filter)
  const chapterReaderIds = useMemo(() => {
    let recordings = data.selectors.getRecordingsForPassage(
      state.selectedBook,
      state.selectedChapter,
      state.startVerse,
      state.endVerse
    );
    if (state.selectedFamily !== 'all') {
      recordings = recordings.filter(r =>
        r.familyId === state.selectedFamily ||
        (r.familyIds && r.familyIds.includes(state.selectedFamily))
      );
    }
    return new Set(recordings.map(r => r.userId));
  }, [data.selectors, data.allRecordings, state.selectedBook, state.selectedChapter, state.startVerse, state.endVerse, state.selectedFamily]);

  // Selected readers that exist in this chapter. When the selection has no
  // recordings here, this is empty — playback falls back to all readers while
  // the user's selection is preserved for chapters where it applies again.
  const effectiveSelectedReaders = useMemo(
    () => state.selectedReaders.filter(id => chapterReaderIds.has(id)),
    [state.selectedReaders, chapterReaderIds]
  );

  // Memoized matching recordings for current passage, respecting active filters
  const matchingRecordings = useMemo(() => {
    let recordings = data.selectors.getRecordingsForPassage(
      state.selectedBook,
      state.selectedChapter,
      state.startVerse,
      state.endVerse
    );

    if (state.selectedFamily !== 'all') {
      recordings = recordings.filter(r =>
        r.familyId === state.selectedFamily ||
        (r.familyIds && r.familyIds.includes(state.selectedFamily))
      );
    }
    if (effectiveSelectedReaders.length > 0) {
      recordings = recordings.filter(r => effectiveSelectedReaders.includes(r.userId));
    }
    // Hide recordings from members whose canRecord permission is off in this family
    if (state.selectedFamily !== 'all' && Object.keys(familyMemberPermissions).length > 0) {
      recordings = recordings.filter(r => {
        const perms = familyMemberPermissions[r.userId];
        return !perms || perms.canRecord !== false;
      });
    }
    if (state.selectedPlan !== 'all') {
      const planData = data.availableListeningPlans?.find(p => p.id === state.selectedPlan)
        || data.availablePassagePlans?.find(p => p.id === state.selectedPlan);
      const sequence = getPlanSequence(planData);
      if (sequence.length > 0) {
        recordings = recordings.filter(r => sequence.some(item =>
          item.book === r.book && item.chapter === r.chapter &&
          r.verse >= item.startVerse && r.verse <= item.endVerse
        ));
      }
    }

    return recordings;
  }, [data.selectors, data.allRecordings, data.availableListeningPlans, data.availablePassagePlans, state.selectedBook, state.selectedChapter, state.startVerse, state.endVerse, state.selectedFamily, effectiveSelectedReaders, state.selectedPlan, familyMemberPermissions]);

  // Reading Plans + Passages Plans in one flat list for the plan picker —
  // the components rendering it just need {id, name}, agnostic to type.
  const allListenablePlans = useMemo(
    () => [...(data.availableListeningPlans || []), ...(data.availablePassagePlans || [])],
    [data.availableListeningPlans, data.availablePassagePlans]
  );

  // Load member permissions for the selected family
  useEffect(() => {
    if (!state.selectedFamily || state.selectedFamily === 'all') {
      setFamilyMemberPermissions({});
      return;
    }
    getDoc(doc(db, 'families', state.selectedFamily))
      .then(snap => setFamilyMemberPermissions(snap.data()?.memberPermissions || {}))
      .catch(() => {});
  }, [state.selectedFamily]);

  // Initialize user data
  useEffect(() => {
    if (currentUser && currentUser.familyIds) {
      console.log(`⏱️ Listen load: familyIds available at ${Math.round(performance.now())}ms since page open — data fetch can start`);
      actions.setUserFamilyIds(currentUser.familyIds);
      actions.setIsInitializing(false);
    } else {
      actions.setIsInitializing(false);
    }
  }, [currentUser]); // Removed actions from dependency array

  // Initial-listen-point decision. Listening-plan data can resolve a render or two
  // after data.loading flips false, so the plan decision lives in its own effect
  // that fires whenever the plan resolves (even late), taking priority.
  const planListenAppliedRef = useRef(false);

  // 1) Active listening plan -> resume at your last point WITHIN the plan
  //    (else the plan's first chapter).
  useEffect(() => {
    if (planListenAppliedRef.current) return;
    const plan = data.currentListeningPlan;
    if (!plan || !Array.isArray(plan.chapters) || plan.chapters.length === 0) return;

    planListenAppliedRef.current = true;
    hasAppliedLastListenRef.current = true; // suppress the no-plan default below

    // Honor the actual last-listened position whenever one exists — "Pick up
    // where I left off" should mean that literally, even if the last chapter
    // you listened to isn't part of the currently active plan (e.g. you
    // browsed a passage manually). Only fall back to the plan's first
    // chapter for a brand-new listener with no cached position at all —
    // this used to require the cached chapter to be IN the plan, so any
    // plan/last-listened mismatch silently reset everyone to chapter one.
    const cached = readListenPoint();
    const target = cached?.book ? cached : plan.chapters[0];
    actions.setSelectedBook(target.book);
    actions.setSelectedChapter(Number(target.chapter) || 1);
  }, [data.currentListeningPlan, actions]);

  // 2) No active listening plan -> Default Listen Point preference:
  //      'whole-bible'   -> advance one chapter in canonical order
  //      'last-listened' -> resume (already restored synchronously from localStorage)
  useEffect(() => {
    if (hasAppliedLastListenRef.current) return;
    if (!currentUser || data.loading) return;
    // If a plan exists (or is still resolving), let the plan effect handle it.
    if (data.currentListeningPlan?.chapters?.length) return;
    hasAppliedLastListenRef.current = true;

    const cached = readListenPoint();
    const pref = currentUser?.defaultListenPoint || 'last-listened';

    if (pref === 'whole-bible' && cached?.book) {
      const idx = BIBLE_BOOKS.findIndex(b => b.id === cached.book);
      const ch = Number(cached.chapter) || 1;
      let next = null;
      if (idx !== -1) {
        if (ch < BIBLE_BOOKS[idx].chapters) next = { book: cached.book, chapter: ch + 1 };
        else if (idx + 1 < BIBLE_BOOKS.length) next = { book: BIBLE_BOOKS[idx + 1].id, chapter: 1 };
      }
      if (next) {
        actions.setSelectedBook(next.book);
        actions.setSelectedChapter(next.chapter);
        return;
      }
    }

    // 'last-listened' (default): localStorage already restored the selection on
    // mount. If there was no local cache, fall back to the saved Firestore value.
    if (!cached) {
      (async () => {
        try {
          const snap = await getDoc(doc(db, 'users', currentUser.uid));
          const last = snap.exists() ? snap.data()?.lastListen : null;
          if (last?.book) {
            actions.setSelectedBook(last.book);
            actions.setSelectedChapter(Number(last.chapter) || 1);
          }
        } catch {}
      })();
    }
  }, [currentUser, data.loading, data.currentListeningPlan, actions]);

  // Update available chapters when book changes
  useEffect(() => {
    actions.setAvailableChapters(availableChapters);
    if (!availableChapters.includes(state.selectedChapter)) {
      actions.setSelectedChapter(1);
    }
  }, [state.selectedBook, availableChapters, state.selectedChapter]); // Removed actions

  // Update available verses when chapter changes
  useEffect(() => {
    if (data.loading) return;

    actions.setAvailableVerses(availableVerses);

    // A Passages Plan navigation just set a specific narrow verse range for
    // this chapter (e.g. Ephesians 2:8-10) — honor it once instead of
    // resetting to the whole chapter below.
    if (pendingVerseRangeRef.current) {
      const { start, end } = pendingVerseRangeRef.current;
      pendingVerseRangeRef.current = null;
      actions.setVerseRange(start, end);
      return;
    }

    // Auto-set verse range based on recorded passages
    const recordedVersesForChapter = data.recordedPassages.filter(p =>
      p.book === state.selectedBook && p.chapter === state.selectedChapter
    );

    // Always set the full chapter range by default (1 to last verse)
    const lastVerse = availableVerses[availableVerses.length - 1] || 31;
    actions.setVerseRange(1, lastVerse);
  }, [state.selectedBook, state.selectedChapter, data.recordedPassages, data.loading, availableVerses]); // Removed actions

  // Update passage text when selection changes
  useEffect(() => {
    actions.setCurrentPassageText(currentPassageText);
  }, [currentPassageText]); // Removed actions

  // Persist last listened passage whenever the selection changes
  useEffect(() => {
    if (!currentUser) return;
    // Fast local cache for instant restore on next visit (no Genesis flash).
    try {
      localStorage.setItem(
        'fb_listenPoint',
        JSON.stringify({
          book: state.selectedBook,
          chapter: state.selectedChapter,
          startVerse: state.startVerse || 1,
          endVerse: state.endVerse || state.startVerse || 1,
        })
      );
    } catch {}
    const save = async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          lastListen: {
            book: state.selectedBook,
            chapter: state.selectedChapter,
            startVerse: state.startVerse || 1,
            endVerse: state.endVerse || state.startVerse || 1,
            updatedAt: serverTimestamp()
          }
        });
      } catch (e) {
        // Non-fatal; don't spam console
      }
    };
    save();
  }, [currentUser, state.selectedBook, state.selectedChapter, state.startVerse, state.endVerse]);

  // Handle verse range validation
  const handleVerseRangeChange = useCallback((newStartVerse, newEndVerse) => {
    if (newEndVerse < newStartVerse) {
      const firstAvailableVerse = availableVerses[0] || 1;
      actions.setVerseRange(firstAvailableVerse, newEndVerse);
    } else {
      actions.setVerseRange(newStartVerse, newEndVerse);
    }
  }, [availableVerses, actions]);

  // Handle verse text click — skip to that verse and play
  const handleVerseTextClick = useCallback((verseNumber) => {
    if (playerRef.current?.playFromVerse) {
      playerRef.current.playFromVerse(verseNumber);
    }
  }, []);

  // Handle play button click
  const handlePlayButtonClick = useCallback(() => {
    console.log('🎵 Play button clicked:', {
      isPlaying: state.isPlaying,
      hasPlayerRef: !!playerRef.current
    });
    if (state.isPlaying) {
      playerRef.current?.pause();
    } else {
      playerRef.current?.playAll();
    }
  }, [state.isPlaying]);

  // Handle verse playback tracking
  const handleVersePlayback = useCallback((verseNumber) => {
    actions.setCurrentlyPlayingVerse(verseNumber);
  }, [actions]);

  // Handle playback state changes — log a listen event on first play per chapter
  const handlePlaybackStateChange = useCallback((isActive, isCurrentlyPlaying) => {
    actions.setIsPlaying(isCurrentlyPlaying);
    if (!isCurrentlyPlaying || !currentUser) return;
    const key = `${state.selectedBook}_${state.selectedChapter}`;
    if (listenLoggedRef.current.has(key)) return;
    listenLoggedRef.current.add(key);
    const bookName = getBookInfo(state.selectedBook)?.name || state.selectedBook;
    const passage = `${bookName} ${state.selectedChapter}`;
    addDoc(collection(db, 'listenEvents'), {
      userId: currentUser.uid,
      userEmail: currentUser.email,
      userName: currentUser.displayName || currentUser.email,
      book: state.selectedBook,
      chapter: state.selectedChapter,
      passage,
      familyIds: state.userFamilyIds || [],
      createdAt: serverTimestamp(),
      type: 'listen',
    }).then(() => {
      // Fire badge check after listen event is logged (non-blocking)
      import('../utils/badgeSystem').then(({ checkAndAwardBadges }) => {
        checkAndAwardBadges(currentUser.uid, db);
      }).catch(() => {});
    }).catch(() => {});
  }, [actions, currentUser, state.selectedBook, state.selectedChapter, state.userFamilyIds]);

  // Handle add note for verse
  const handleAddNoteForVerse = useCallback((verseNumber) => {
    actions.setNoteForVerse(verseNumber);
    actions.setShowStudyNoteEditor(true);
  }, [actions]);

  // Long-press a verse -> "Add to Passages Plan" popover. The plan list is
  // the user's own family's Passages Plans (the only ones they're allowed to
  // edit — see firestore.rules), fetched lazily on first open rather than as
  // part of the main data pipeline, since this is an occasional action.
  const [addToPlanOpen, setAddToPlanOpen] = useState(false);
  const [addToPlanVerseRef, setAddToPlanVerseRef] = useState(null);
  const [familyPassagePlans, setFamilyPassagePlans] = useState(null); // null = not yet loaded

  const handleVerseLongPress = useCallback((verseNumber) => {
    const bookInfo = getBookInfo(state.selectedBook);
    setAddToPlanVerseRef({
      book: state.selectedBook,
      bookName: bookInfo?.name || state.selectedBook,
      chapter: state.selectedChapter,
      startVerse: verseNumber,
      endVerse: verseNumber,
    });
    setAddToPlanOpen(true);

    if (familyPassagePlans === null) {
      const familyId = currentUser?.familyIds?.[0];
      if (!familyId) {
        setFamilyPassagePlans([]);
      } else {
        getDocs(query(collection(db, 'passagePlans'), where('familyId', '==', familyId)))
          .then(snap => setFamilyPassagePlans(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
          .catch(err => { console.error('Error loading family passage plans:', err); setFamilyPassagePlans([]); });
      }
    }
  }, [state.selectedBook, state.selectedChapter, currentUser, familyPassagePlans]);

  const handleAddVerseToPlan = useCallback(async (planId) => {
    const planRef = doc(db, 'passagePlans', planId);
    const planDoc = await getDoc(planRef);
    if (!planDoc.exists()) throw new Error('Plan not found.');
    const existingPassages = planDoc.data().passages || [];

    const alreadyThere = existingPassages.some(p =>
      p.book === addToPlanVerseRef.book && p.chapter === addToPlanVerseRef.chapter &&
      addToPlanVerseRef.startVerse >= p.startVerse && addToPlanVerseRef.startVerse <= p.endVerse
    );
    if (!alreadyThere) {
      // addToPlanVerseRef already carries {book, bookName, chapter,
      // startVerse, endVerse} — the same shape parsePassageSegment produces
      // — so write it as-is rather than reconstructing a subset that could
      // drift out of sync with it (bookName is required by formatPassageLabel).
      await updateDoc(planRef, {
        passages: [...existingPassages, addToPlanVerseRef],
        updatedAt: serverTimestamp(),
      });
      // Reflect the addition locally so the dropdown/plan data stays fresh
      // without a full refetch.
      setFamilyPassagePlans(prev => prev?.map(p => p.id === planId
        ? { ...p, passages: [...existingPassages, addToPlanVerseRef] }
        : p
      ) || null);
    }

    if (currentUser?.uid) {
      updateDoc(doc(db, 'users', currentUser.uid), { lastPassagePlanId: planId }).catch(() => {});
    }
  }, [addToPlanVerseRef, currentUser]);

  // Per-verse family note counts for the current chapter (powers the note marker).
  const [verseNoteCounts, setVerseNoteCounts] = useState({});
  const [notesRefreshTick, setNotesRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const familyIds = state.userFamilyIds || currentUser?.familyIds || [];
    if (!currentUser || !state.selectedBook || !state.selectedChapter) {
      setVerseNoteCounts({});
      return;
    }
    getChapterNoteCounts(currentUser, familyIds, state.selectedBook, state.selectedChapter)
      .then((counts) => { if (!cancelled) setVerseNoteCounts(counts || {}); })
      .catch(() => { if (!cancelled) setVerseNoteCounts({}); });
    return () => { cancelled = true; };
  }, [currentUser, state.userFamilyIds, state.selectedBook, state.selectedChapter, notesRefreshTick]);

  // Handle verse favorite — toggles Firestore + local state
  const handleVerseFavorite = useCallback(async (verseNumber) => {
    if (!currentUser) return;
    const verseKey = `${state.selectedBook}_${state.selectedChapter}_${verseNumber}`;
    const isFav = state.favoritedVerses.has(verseKey);
    if (isFav) {
      // Remove from Firestore
      actions.removeFavoriteVerse(verseKey);
      try {
        const { getDocs: gd, query: q, where: w, deleteDoc: dd, doc: d } = await import('firebase/firestore');
        const snap = await gd(q(
          collection(db, `users/${currentUser.uid}/favoritedVerses`),
          w('book', '==', state.selectedBook),
          w('chapter', '==', state.selectedChapter),
          w('verse', '==', verseNumber)
        ));
        for (const docSnap of snap.docs) await dd(d(db, `users/${currentUser.uid}/favoritedVerses`, docSnap.id));
      } catch (e) { console.warn('unfavorite error', e); }
    } else {
      // Add to Firestore
      actions.addFavoriteVerse(verseKey);
      try {
        const bookInfo = getBookInfo(state.selectedBook);
        const verseText = getVerseText(state.selectedBook, state.selectedChapter, verseNumber);
        const passage = `${bookInfo?.name || state.selectedBook} ${state.selectedChapter}:${verseNumber}`;
        await addDoc(collection(db, `users/${currentUser.uid}/favoritedVerses`), {
          book: state.selectedBook,
          chapter: state.selectedChapter,
          verse: verseNumber,
          verseText: verseText || '',
          passage,
          favoritedAt: new Date(),
          familyIds: currentUser.familyIds || [],
          displayName: currentUser.displayName || currentUser.email || 'Family Member',
        });
      } catch (e) { console.warn('favorite error', e); }
    }
  }, [currentUser, state.selectedBook, state.selectedChapter, state.favoritedVerses, actions]);

  // Resolves whichever plan is currently active for navigation purposes —
  // either type, via the unified sequence shape (see utils/planPlayback).
  const getActivePlanForNav = useCallback(() => {
    if (state.selectedPlan === 'none') return null;
    if (state.selectedPlan !== 'all') {
      return data.availableListeningPlans?.find(p => p.id === state.selectedPlan)
        || data.availablePassagePlans?.find(p => p.id === state.selectedPlan)
        || null;
    }
    return data.currentListeningPlan;
  }, [state.selectedPlan, data.availableListeningPlans, data.availablePassagePlans, data.currentListeningPlan]);

  // Chapter/passage navigation — follows the active plan's declared order if
  // one exists (and isn't overridden to 'none'), otherwise sequential Bible
  // order. Returns true if it actually advanced to a next item, false if
  // there wasn't one (used by continuous playback to know when to stop).
  const navigateToNextPlanChapter = useCallback(() => {
    const plan = getActivePlanForNav();
    const sequence = getPlanSequence(plan);
    if (sequence.length > 0) {
      const idx = findSequenceIndex(sequence, state.selectedBook, state.selectedChapter, state.startVerse);
      if (idx >= 0 && idx < sequence.length - 1) {
        const next = sequence[idx + 1];
        pendingVerseRangeRef.current = { start: next.startVerse, end: next.endVerse };
        actions.setSelectedBook(next.book);
        actions.setSelectedChapter(next.chapter);
        return true;
      }
      return false;
    }
    // No plan — advance by Bible order
    const bookInfo = BIBLE_BOOKS.find(b => b.id === state.selectedBook);
    if (bookInfo && state.selectedChapter < bookInfo.chapters) {
      actions.setSelectedChapter(state.selectedChapter + 1);
      return true;
    } else {
      const bookIdx = BIBLE_BOOKS.findIndex(b => b.id === state.selectedBook);
      if (bookIdx < BIBLE_BOOKS.length - 1) {
        actions.setSelectedBook(BIBLE_BOOKS[bookIdx + 1].id);
        actions.setSelectedChapter(1);
        return true;
      }
    }
    return false;
  }, [getActivePlanForNav, state.selectedBook, state.selectedChapter, state.startVerse, actions]);

  // Continuous playback: when the current queue naturally runs out (not a
  // manual pause), auto-advance to the plan's next chapter/passage and flag
  // the next MultiAudioPlayer queue to start playing immediately. Scoped to
  // an active plan only — plan-less browsing still just stops at chapter end,
  // matching prior behavior.
  const handleQueueEnded = useCallback(() => {
    const sequence = getPlanSequence(getActivePlanForNav());
    if (sequence.length === 0) return;
    const advanced = navigateToNextPlanChapter();
    if (advanced) setAutoPlayNextQueue(true);
  }, [getActivePlanForNav, navigateToNextPlanChapter]);

  // Reset the one-shot autoplay flag after MultiAudioPlayer's re-init effect
  // (a child effect, so it always runs first in the same commit) has read it.
  useEffect(() => {
    if (autoPlayNextQueue) setAutoPlayNextQueue(false);
  }, [matchingRecordings]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToPreviousPlanChapter = useCallback(() => {
    const plan = getActivePlanForNav();
    const sequence = getPlanSequence(plan);
    if (sequence.length > 0) {
      const idx = findSequenceIndex(sequence, state.selectedBook, state.selectedChapter, state.startVerse);
      if (idx > 0) {
        const prev = sequence[idx - 1];
        pendingVerseRangeRef.current = { start: prev.startVerse, end: prev.endVerse };
        actions.setSelectedBook(prev.book);
        actions.setSelectedChapter(prev.chapter);
        return true;
      }
      return false;
    }
    // No plan — go back by Bible order
    if (state.selectedChapter > 1) {
      actions.setSelectedChapter(state.selectedChapter - 1);
      return true;
    } else {
      const bookIdx = BIBLE_BOOKS.findIndex(b => b.id === state.selectedBook);
      if (bookIdx > 0) {
        const prevBook = BIBLE_BOOKS[bookIdx - 1];
        actions.setSelectedBook(prevBook.id);
        actions.setSelectedChapter(prevBook.chapters);
        return true;
      }
    }
    return false;
  }, [getActivePlanForNav, state.selectedBook, state.selectedChapter, state.startVerse, actions]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const familyDropdown = event.target.closest('[data-family-dropdown]');
      const readerDropdown = event.target.closest('[data-reader-dropdown]');
      const planDropdown = event.target.closest('[data-plan-dropdown]');
      const verseAccordion = event.target.closest('[data-verse-accordion]');
      const searchAccordion = event.target.closest('[data-search-accordion]');
      
      if (state.showFamilyDropdown && !familyDropdown) {
        actions.toggleFamilyDropdown();
      }
      if (state.showReaderDropdown && !readerDropdown) {
        actions.toggleReaderDropdown();
      }
      if (state.showPlanDropdown && !planDropdown) {
        actions.togglePlanDropdown();
      }
      if (state.showVerseAccordion && !verseAccordion) {
        actions.setShowVerseAccordion(false);
      }
      if (state.showSearchAccordion && !searchAccordion) {
        actions.toggleSearchAccordion();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [state.showFamilyDropdown, state.showReaderDropdown, state.showPlanDropdown, state.showVerseAccordion, state.showSearchAccordion]); // Removed actions

  // Cleanup audio when component unmounts
  useEffect(() => {
    return () => {
      // Copy playerRef.current to a variable for cleanup
      const player = playerRef.current;
      if (player) {
        player.cleanup();
      }
    };
  }, []);

  // Show loading state during initialization
  if (state.isInitializing) {
    return (
      <div className="bg-white" style={{ minHeight: '100vh', paddingBottom: '80px' }}>
        <div 
          className="bg-white border-b border-gray-200/50 shadow-sm" 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            zIndex: 9999,
            transform: 'translateZ(0)',
            willChange: 'transform'
          }}
        >
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-6 h-6"></div>
                  <div className="h-6 w-32"></div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="h-6 w-6"></div>
                  <div className="h-6 w-6"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 pt-32 pb-6" style={{ paddingTop: '160px' }}>
        </div>
      </div>
    );
  }

  // Show sign in prompt
  if (!currentUser) {
    return (
      <div className="bg-white" style={{ minHeight: '100vh', paddingBottom: '80px' }}>
        <div 
          className="bg-white border-b border-gray-200/50 shadow-sm" 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            zIndex: 9999,
            transform: 'translateZ(0)',
            willChange: 'transform'
          }}
        >
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-6 h-6"></div>
                  <div className="h-6 w-32"></div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="h-6 w-6"></div>
                  <div className="h-6 w-6"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 pt-32 pb-6 sm:pb-8" style={{ paddingTop: '160px' }}>
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Please Sign In</h2>
            <p className="text-gray-600 mb-6">Sign in to access family recordings</p>
            <button className="btn-primary w-full sm:w-auto" onClick={() => navigate('/auth')}>
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show no family access
  if (state.userFamilyIds.length === 0) {
    return (
      <div className="bg-white" style={{ minHeight: '100vh', paddingBottom: '80px' }}>
      <div 
        className="bg-white border-b border-gray-200/50 shadow-sm" 
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          zIndex: 9999,
          transform: 'translateZ(0)',
          willChange: 'transform'
        }}
      >
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-6 h-6"></div>
                  <div className="h-6 w-32"></div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="h-6 w-6"></div>
                  <div className="h-6 w-6"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 pt-32 pb-6 sm:pb-8" style={{ paddingTop: '160px' }}>
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">No Family Access</h2>
            <p className="text-gray-600 mb-6">
              You need to create or join a family to access recordings.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button className="btn-primary w-full sm:w-auto" onClick={() => navigate('/create-family')}>
                Create Family
              </button>
              <button className="btn-secondary w-full sm:w-auto" onClick={() => navigate('/join-family')}>
                Join Family
                    </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#0d1520] pt-[64px] lg:pt-[136px] pb-[120px] lg:pb-24" style={{ minHeight: '100vh' }}>
      {/* pt above clears the fixed top navigation once, so the filter bar and
          text both flow cleanly below it (no per-element doubled padding). */}
      {/* Top Navigation */}
      <TopNavigation
        selectedBook={state.selectedBook}
        selectedChapter={state.selectedChapter}
        startVerse={state.startVerse}
        endVerse={state.endVerse}
        selectedPlan={state.selectedPlan}
        availableListeningPlans={allListenablePlans}
        currentListeningPlan={data.currentListeningPlan}
        isLoadingRecordings={data.loading}
        showNotesDrawer={state.showNotesDrawer}
        onTogglePassageSelector={actions.togglePassageSelector}
        onToggleBottomSheet={() => setBottomSheetExpanded(e => !e)}
        onToggleNotesDrawer={() => actions.setShowNotesDrawer(true)}
        onNavigateToPreviousPlanChapter={navigateToPreviousPlanChapter}
        onNavigateToNextPlanChapter={navigateToNextPlanChapter}
        onSetSelectedPlan={actions.setSelectedPlan}
      />

      {/* Passage Selector */}
      <PassageSelector
        showPassageSelector={state.showPassageSelector}
        selectedBook={state.selectedBook}
        selectedChapter={state.selectedChapter}
        startVerse={state.startVerse}
        endVerse={state.endVerse}
        availableChapters={availableChapters}
        availableVerses={availableVerses}
        recordedPassages={data.recordedPassages}
        onSetSelectedBook={actions.setSelectedBook}
        onSetSelectedChapter={actions.setSelectedChapter}
        onSetVerseRange={handleVerseRangeChange}
        onClose={actions.togglePassageSelector}
      />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 pb-20">
        <div className="space-y-6">
          {/* Bible Text Display */}
          {data.hasSearchedForRecordings && (
              <div className="bg-white dark:bg-[#0d1520] py-6 animate-fade-in">
                <div className="max-w-5xl mx-auto px-4">
                <BibleTextDisplay
                  selectedBook={state.selectedBook}
                  selectedChapter={state.selectedChapter}
                  startVerse={state.startVerse}
                  endVerse={state.endVerse}
                  selectedReaders={state.selectedReaders}
                  matchingRecordings={matchingRecordings}
                  currentlyPlayingVerse={state.currentlyPlayingVerse}
                  verseNotes={verseNoteCounts}
                  onVerseTextClick={handleVerseTextClick}
                  onAddNoteForVerse={handleAddNoteForVerse}
                  onVerseLongPress={handleVerseLongPress}
                  versesPerBlock={Number(currentUser?.listenVersesPerBlock) || 3}
                />
                </div>
              </div>
          )}

          {/* Audio Player */}
            {matchingRecordings.length > 0 ? (
              <div className="animate-fade-in-delay-1">
                <MultiAudioPlayer
                  ref={playerRef}
                  recordings={matchingRecordings}
                  autoPlayNewQueue={autoPlayNextQueue}
                  trimSilence={trimSilence}
                  enhanceAudio={enhanceAudio}
                  playbackRate={playbackRate}
                  onVersePlayback={handleVersePlayback}
                  onPlaybackStateChange={handlePlaybackStateChange}
                  onLoadingStateChange={actions.setIsLoadingRecordings}
                  onQueueEnded={handleQueueEnded}
                />
              </div>
          ) : data.hasSearchedForRecordings && matchingRecordings.length === 0 ? (
              <div className="bg-white dark:bg-[#111827] rounded-3xl shadow-sm border border-gray-200/50 dark:border-white/10 p-8 animate-fade-in-delay-1">
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8 text-gray-400 dark:text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">No recordings found</h3>
                  <p className="text-gray-600 dark:text-white/60 mb-4 max-w-md mx-auto">
                    No recordings found for this passage. Try selecting a different passage, adjusting your filters, or record this one yourself.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-white/40 mb-6 max-w-md mx-auto">
                    If you would like to just read the text without audio, just choose a start and end verse above.
                  </p>
                  <button 
                    onClick={() => {
                      const params = new URLSearchParams({
                      book: state.selectedBook,
                      chapter: state.selectedChapter.toString(),
                      start: state.startVerse?.toString() || '1',
                      end: state.endVerse?.toString() || '1'
                      });
                      navigate(`/record?${params.toString()}`);
                    }}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    <span>Record This Passage</span>
                  </button>
                </div>
              </div>
          ) : data.loading ? (
            <div></div>
          ) : null}
        </div>
      </div>

      {/* Bottom Player Sheet — mobile only */}
      <BottomPlayerSheet
        isExpanded={bottomSheetExpanded}
        onExpandChange={setBottomSheetExpanded}
        isPlaying={state.isPlaying}
        hasRecordings={matchingRecordings.length > 0}
        onPlay={handlePlayButtonClick}
        onPrev={navigateToPreviousPlanChapter}
        onNext={navigateToNextPlanChapter}
        selectedFamily={state.selectedFamily}
        selectedReaders={state.selectedReaders}
        selectedPlan={state.selectedPlan}
        userFamilies={data.userFamilies}
        availableReaders={data.availableReaders}
        availableListeningPlans={allListenablePlans}
        chapterReaderIds={chapterReaderIds}
        trimSilence={trimSilence}
        onSetTrimSilence={handleSetTrimSilence}
        onSetSelectedFamily={actions.setSelectedFamily}
        onSetSelectedReaders={actions.setSelectedReaders}
        onSetSelectedPlan={actions.setSelectedPlan}
        playbackRate={playbackRate}
        onSetPlaybackRate={handleSetPlaybackRate}
        passageLabel={`${state.selectedBook} ${state.selectedChapter}`}
      />

      {/* Study Notes Drawer */}
      <StudyNotesDrawer
        isOpen={state.showNotesDrawer}
        onClose={() => {
          actions.setShowNotesDrawer(false);
          setNotesRefreshTick((t) => t + 1);
        }}
        book={state.selectedBook}
        chapter={state.selectedChapter}
        currentUser={currentUser}
        familyMembers={data.userFamilies}
      />

      {/* Study Note Editor Modal */}
      <StudyNoteEditor
        isOpen={state.showStudyNoteEditor}
        onClose={() => {
          actions.setShowStudyNoteEditor(false);
          actions.setNoteForVerse(null);
        }}
        book={state.selectedBook}
        chapter={state.selectedChapter}
        verse={state.noteForVerse || (state.startVerse === state.endVerse ? state.startVerse : null)}
        verseRange={!state.noteForVerse && state.startVerse !== state.endVerse ? { start: state.startVerse, end: state.endVerse } : null}
        isFavorited={state.noteForVerse ? state.favoritedVerses.has(`${state.selectedBook}_${state.selectedChapter}_${state.noteForVerse}`) : false}
        onToggleFavorite={state.noteForVerse ? () => handleVerseFavorite(state.noteForVerse) : undefined}
        onSave={() => {
          // Refresh notes detection (re-color the verse markers)
          actions.setNoteForVerse(null);
          setNotesRefreshTick((t) => t + 1);
        }}
      />

      {/* Add to Passages Plan — opened by long-pressing a verse */}
      <AddToPlanPopover
        isOpen={addToPlanOpen}
        onClose={() => setAddToPlanOpen(false)}
        verseRef={addToPlanVerseRef}
        plans={familyPassagePlans || []}
        defaultPlanId={currentUser?.lastPassagePlanId}
        onAdd={handleAddVerseToPlan}
        isFavorited={addToPlanVerseRef ? state.favoritedVerses.has(`${addToPlanVerseRef.book}_${addToPlanVerseRef.chapter}_${addToPlanVerseRef.verse}`) : false}
        onToggleFavorite={addToPlanVerseRef ? () => handleVerseFavorite(addToPlanVerseRef.verse) : undefined}
      />

      {/* Verse Action Accordion - rendered as portal */}
      {state.showVerseAccordion && createPortal(
        <>
          <style>
            {`
              @keyframes slideUp {
                from {
                  transform: translateY(100%);
                }
                to {
                  transform: translateY(0);
                }
              }
            `}
          </style>
          <div 
            data-verse-accordion
            style={{
              position: 'fixed',
              bottom: '80px',
              left: '0',
              right: '0',
              zIndex: 100,
              backgroundColor: 'white',
              borderTop: '1px solid #e5e7eb',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              animation: 'slideUp 0.3s ease-out',
              maxHeight: 'calc(60vh - 20px)',
              minHeight: '72px',
              overflowY: 'auto'
            }}
          >
            <div className="max-w-6xl mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-cerulean rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{state.selectedVerseForAction}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{getBookInfo(state.selectedBook)?.name} {state.selectedChapter}:{state.selectedVerseForAction}</h3>
                    <p className="text-sm text-gray-400">By: {currentUser?.displayName || currentUser?.email || 'User'}</p>
                  </div>
                </div>
                
              <div className="flex items-center space-x-2">
                {/* Heart/Favorite Button */}
                <button
                    onClick={() => handleVerseFavorite(state.selectedVerseForAction)}
                  className="p-2 hover:bg-coral hover:bg-opacity-10 rounded-lg transition-colors duration-200"
                >
                  <svg 
                    className="w-5 h-5" 
                      fill={state.favoritedVerses.has(`${state.selectedBook}_${state.selectedChapter}_${state.selectedVerseForAction}`) ? '#FD494F' : 'none'} 
                    stroke="#FD494F" 
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                    />
                  </svg>
                </button>
                
                <button
                    onClick={() => {
                      actions.setNoteForVerse(state.selectedVerseForAction);
                      actions.setShowStudyNoteEditor(true);
                      actions.setShowVerseAccordion(false);
                    }}
                  className="flex items-center space-x-2 px-4 py-2 bg-cerulean text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-sm font-medium">Add Note</span>
                </button>
                
                <button
                    onClick={() => actions.setShowVerseAccordion(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        </>,
        document.body
      )}

    </div>
  );
}

export default Listen; 
